import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Check, BookOpen, ArrowUpRight } from "lucide-react";
import { MODES } from "@/data/product";
import { SectionHeading } from "@/components/common/ui";

const DEPTH = { Blue: 26, Pink: 52, Red: 74, "Near-Infrared": 94 };
const LAYERS = [
  { name: "Epidermis", h: "26%", bg: "#FBEAD9" },
  { name: "Dermis", h: "44%", bg: "#F6D8C0" },
  { name: "Hypodermis", h: "30%", bg: "#EFC9AE" },
];

export const LightSpectrum = () => {
  const [active, setActive] = useState(0);
  const m = MODES[active];
  const depth = DEPTH[m.name] ?? 60;
  const layer = depth > 80 ? "hypodermis" : depth > 45 ? "dermis" : "epidermis";
  return (
    <section className="bg-[#FFF0E8] py-24 md:py-32" data-testid="light-spectrum">
      <div className="mx-auto max-w-[1100px] px-6 md:px-12">
        <SectionHeading align="center" overline="Four Wavelengths · Six Modes"
          title={<>Science-Backed <span className="text-[#E97C6A]">LED Light Therapy</span></>}
          sub="Discover how each wavelength targets a different skin concern, helping you achieve clearer, firmer and more radiant-looking skin." className="mb-12" />

        {/* Horizontal mode tabs */}
        <div className="mb-10 flex flex-wrap justify-center gap-2.5">
          {MODES.map((item, i) => {
            const on = i === active;
            return (
              <button key={item.name} onClick={() => setActive(i)} onMouseEnter={() => setActive(i)} data-testid={`light-${i}`}
                style={on ? { backgroundColor: item.swatch + "26", borderColor: item.swatch } : {}}
                className={`flex items-center gap-2.5 rounded-full border px-4 py-2.5 transition-all ${on ? "shadow-sm" : "border-[#F2E8E6] bg-white hover:border-[#FBC4A8]"}`}>
                <span className="h-3.5 w-3.5 rounded-full ring-2 ring-white" style={{ backgroundColor: item.swatch, boxShadow: on ? `0 0 10px ${item.swatch}` : "none" }} />
                <span className="font-display text-sm text-[#2C2B2C] md:text-base">{item.name}</span>
                <span className="text-[0.7rem] text-[#8A7F86]">{item.nm}</span>
              </button>
            );
          })}
        </div>

        {/* Focal detail panel */}
        <div className="relative overflow-hidden rounded-[2.5rem] border border-[#F2E8E6] bg-[#FFFCFA] p-8 md:p-12">
          <div className="pointer-events-none absolute -right-24 -top-24 h-72 w-72 rounded-full blur-3xl transition-colors duration-500" style={{ backgroundColor: m.swatch + "33" }} />
          <AnimatePresence mode="wait">
            <motion.div key={active} initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -16 }} transition={{ duration: 0.4, ease: [0.22, 1, 0.36, 1] }}
              className="relative grid items-center gap-10 lg:grid-cols-[1.15fr_0.85fr] lg:gap-14">
              {/* Left: copy */}
              <div>
                <div className="flex items-center gap-3">
                  <span className="h-6 w-6 rounded-full ring-4 ring-white" style={{ backgroundColor: m.swatch, boxShadow: `0 0 18px ${m.swatch}` }} />
                  <p className="font-display text-3xl font-light text-[#2C2B2C]">{m.name} <span className="text-lg text-[#8A7F86]">· {m.nm}</span></p>
                </div>
                <p className="mt-4 text-base font-light leading-relaxed text-[#6E6168]">{m.desc}</p>
                {m.benefits && (
                  <ul className="mt-6 grid gap-2.5 sm:grid-cols-2" data-testid="light-benefits">
                    {m.benefits.map((b) => (
                      <li key={b} className="flex items-center gap-2.5">
                        <span className="flex h-5 w-5 flex-none items-center justify-center rounded-full bg-[#FBC4A8]">
                          <Check className="h-3 w-3 text-[#FFFFFF]" strokeWidth={3} />
                        </span>
                        <span className="text-sm text-[#2C2B2C]">{b}</span>
                      </li>
                    ))}
                  </ul>
                )}
                {m.study && (
                  <a href={m.study.url} target="_blank" rel="noreferrer" data-testid="light-study-link"
                    className="group mt-7 flex items-center gap-2 border-t border-[#F0E7DC] pt-4 text-xs text-[#8A7F86] transition-colors hover:text-[#FBC4A8]">
                    <BookOpen className="h-3.5 w-3.5 flex-none" />
                    <span className="flex-1">Clinical source: {m.study.ref}</span>
                    <ArrowUpRight className="h-3.5 w-3.5 flex-none transition-transform group-hover:-translate-y-0.5 group-hover:translate-x-0.5" />
                  </a>
                )}
              </div>

              {/* Right: penetration diagram */}
              <div className="flex items-center justify-center gap-6 rounded-[1.75rem] bg-white/70 p-6 ring-1 ring-[#F2E8E6]">
                <div className="relative h-64 w-36 flex-none overflow-hidden rounded-2xl border border-[#F2E8E6]">
                  <div className="absolute inset-0 flex flex-col">
                    {LAYERS.map((l) => (
                      <div key={l.name} className="flex items-start justify-end px-2 pt-1" style={{ height: l.h, backgroundColor: l.bg }}>
                        <span className="text-[0.52rem] uppercase tracking-wider text-[#2C2B2C]/55">{l.name}</span>
                      </div>
                    ))}
                  </div>
                  <motion.div className="absolute left-1/2 top-0 w-2 -translate-x-1/2 rounded-b-full" initial={{ height: 0 }} animate={{ height: `${depth}%` }} transition={{ duration: 0.7, ease: "easeOut" }} style={{ background: `linear-gradient(to bottom, ${m.swatch}, ${m.swatch}00)` }} />
                  <motion.span className="absolute left-1/2 h-3.5 w-3.5 -translate-x-1/2 -translate-y-1/2 rounded-full" initial={{ top: 0, opacity: 0 }} animate={{ top: `${depth}%`, opacity: 1 }} transition={{ duration: 0.7, ease: "easeOut" }} style={{ backgroundColor: m.swatch, boxShadow: `0 0 16px ${m.swatch}` }} />
                </div>
                <div>
                  <p className="text-[0.65rem] uppercase tracking-[0.18em] text-[#8A7F86]">Penetration depth</p>
                  <p className="font-display text-5xl text-[#2C2B2C]">{depth}%</p>
                  <p className="mt-2 max-w-[11rem] text-sm font-light text-[#6E6168]">For {m.for.toLowerCase()}, reaching deep into the {layer}.</p>
                </div>
              </div>
            </motion.div>
          </AnimatePresence>
        </div>
      </div>
    </section>
  );
};
